"use client";

import { useTranslations } from "next-intl";

import {
	Dialog,
	DialogContent,
	type DialogProps,
	DialogTitle,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme
} from "@/components/controls/base-controls";
import { CloseIcon } from "@/components/controls/icons";
import AirportSearchForm from "@/components/forms/airport-search.form";
import type { Airport } from "@/core/types/airport.type";
import type { Direction } from "@/core/types/direction.type";

const AirportsDialog = ({
	selectedAirport,
    direction,
	open,
	onHandleDialogClose,
	scroll="paper",
}: {
	selectedAirport: Airport | undefined;
    direction: Direction;
	open: boolean;
	onHandleDialogClose: (value: Airport | undefined) => void;
	scroll: DialogProps["scroll"];
}) => {
	const t = useTranslations();

    const theme = useTheme();
    // Returns true if screen width is 'sm' (default 600px) or smaller
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const handleClose = () => {
		try {
			onHandleDialogClose(selectedAirport);
		} catch (error) {
			console.error("Error handling dialog close:", error);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
            fullScreen={isMobile}
			scroll={scroll} 
			disableRestoreFocus={true}
			aria-labelledby="dialog-title"
			aria-describedby="dialog-description"
            slotProps={{
                paper: {
                sx: {
                    [theme.breakpoints.up('md')]: {
						height: "26.5em",
						maxHeight: "80vh",
						width: "36em",  
						maxWidth: "60vw"
					}
				}}
            }}
		>
            <DialogTitle sx={{
				backgroundColor: "var(--mui-palette-dialog-title-bg)",
				display: "flex",
				flexDirection: "row",
				justifyContent: "space-between",
				alignItems: "center",
				margin: "0 auto",
				padding: ".6em .6em .6em .8em",
				width: "100%",
			}}>
				<Typography
					variant="body1"
					sx={{
						color: "var(--mui-palette-lightest)",
						mt: { xs: "4px", md: "3px" },
					}}
				>
					{t(`airport.select.${direction}`)}
				</Typography>
				<IconButton	
					onClick={handleClose}
					aria-label="close"
					sx={{ color: "var(--mui-palette-lightest)" }}
				>
					<CloseIcon />
				</IconButton>
			</DialogTitle>

			<DialogContent dividers sx={{
				backgroundColor: "var(--mui-palette-page-bg)",
				padding: "1em",
				pb: { md: "2.5em" }, // Adds more bottom padding for desktop view
				display: "flex",
				flexDirection: "column",
			}}>
				<AirportSearchForm 
					selectedAirport={selectedAirport} 
					onAirportSelect={(value, shouldClose = true) => {
						if (shouldClose) {
							onHandleDialogClose(value); // Closes the dialog completely
						} else {
							// If clear button was tapped, update the parent selection variables 
							// to empty object status but leave the modal open so they can re-type!
							onHandleDialogClose(value); 
							// NOTE: If your main FlightForm component sets dialogOpen to false inside onHandleDialogClose,
							// create a separate conditional assignment inside your flight form close handler method instead!
						}
					}} 
				/>
			</DialogContent>
			
		</Dialog>
	);
};

export default AirportsDialog;
